import {
  AsyncSelectFetchParams,
  AsyncSelectFetchResult,
} from "@/components/form/types";

export type SoftSkillVendorStatus = "active" | "inactive" | "pending";

export interface SoftSkillVendorType {
  _id: string;
  vendorName: string; // e.g. "SkillUp Training"
  contactPerson: string; // e.g. "Rachel Green"
  email: string; // e.g. "rachel@skillup.com"
  phone: string;
  expertise: string[]; // Renamed from specialization
  city: string;
  state: string;
  preferredDepartments: string[];
  rating: number; // e.g. 4.5
  completedPrograms: number; // e.g. 10
  coursesOffered: string;
  description: string;
  status: SoftSkillVendorStatus;
  createdAt: string;
  updatedAt: string;
}

export interface SoftSkillVendorCreateType {
  vendorName: string;
  contactPerson: string;
  email: string;
  phone: string;
  expertise: string[];
  city: string;
  state: string;
  preferredDepartments: string[];
  rating: number;
  completedPrograms: number;
  coursesOffered: string;
  description: string;
  status: SoftSkillVendorStatus;
}

export interface SoftSkillVendorMetaType {
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

export type SoftSkillVendorParamsType = {
  search?: string;
  status?: SoftSkillVendorStatus | "all";
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  page?: number;
  limit?: number;
};

// ─── Dummy Data ───────────────────────────────────────────────────────────────

const DUMMY_SOFT_SKILL_VENDORS: SoftSkillVendorType[] = [
  {
    _id: "1",
    vendorName: "SkillUp Training",
    contactPerson: "Rachel Green",
    email: "rachel@skillup.com",
    phone: "+1-555-0101",
    expertise: ["Communication", "Leadership"],
    city: "New York",
    state: "NY",
    preferredDepartments: ["1", "2"],
    rating: 4.5,
    completedPrograms: 10,
    coursesOffered: "Communication, Leadership 101",
    description: "Top tier soft skills training.",
    status: "active",
    createdAt: "2024-01-01T00:00:00.000Z",
    updatedAt: "2024-01-01T00:00:00.000Z",
  },
  {
    _id: "2",
    vendorName: "LeadersEdge Institute",
    contactPerson: "Tom Wilson",
    email: "tom@leadersedge.com",
    phone: "+1-555-0102",
    expertise: ["Team Building", "Public Speaking"],
    city: "Chicago",
    state: "IL",
    preferredDepartments: ["3", "4"],
    rating: 4.2,
    completedPrograms: 6,
    coursesOffered: "Team Dynamics, Advanced Speaking",
    description: "Building leaders for tomorrow.",
    status: "active",
    createdAt: "2024-01-02T00:00:00.000Z",
    updatedAt: "2024-01-02T00:00:00.000Z",
  },
  {
    _id: "3",
    vendorName: "ProDevelopment Academy",
    contactPerson: "Nancy Brown",
    email: "nancy@prodevelopment.com",
    phone: "+1-555-0103",
    expertise: ["Time Management", "Interview Skills"],
    city: "Austin",
    state: "TX",
    preferredDepartments: ["1"],
    rating: 4.8,
    completedPrograms: 15,
    coursesOffered: "Time Mastery, Ace the Interview",
    description: "Practical skills for professionals.",
    status: "active",
    createdAt: "2024-01-03T00:00:00.000Z",
    updatedAt: "2024-01-03T00:00:00.000Z",
  },
  {
    _id: "4",
    vendorName: "MindGrowth Solutions",
    contactPerson: "Kevin Patel",
    email: "kevin@mindgrowth.com",
    phone: "+1-555-0104",
    expertise: ["Critical Thinking", "Emotional Intelligence"],
    city: "San Francisco",
    state: "CA",
    preferredDepartments: ["2", "3"],
    rating: 3.9,
    completedPrograms: 4,
    coursesOffered: "Thinking Fast, EQ Basics",
    description: "Focus on cognitive growth.",
    status: "pending",
    createdAt: "2024-01-04T00:00:00.000Z",
    updatedAt: "2024-01-04T00:00:00.000Z",
  },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

const simulateDelay = (ms = 400) =>
  new Promise((resolve) => setTimeout(resolve, ms));

const applyFilters = (
  vendors: SoftSkillVendorType[],
  filters: SoftSkillVendorParamsType
): { data: SoftSkillVendorType[]; total: number } => {
  let result = [...vendors];
  if (filters.search) {
    const q = filters.search.toLowerCase();
    result = result.filter(
      (v) =>
        v.vendorName.toLowerCase().includes(q) ||
        v.contactPerson.toLowerCase().includes(q) ||
        v.email.toLowerCase().includes(q) ||
        v.expertise.some((s) => s.toLowerCase().includes(q))
    );
  }
  if (filters.status && filters.status !== "all") {
    result = result.filter((v) => v.status === filters.status);
  }
  const total = result.length;
  const page = filters.page ?? 1;
  const limit = filters.limit ?? 10;
  result = result.slice((page - 1) * limit, page * limit);
  return { data: result, total };
};

// ─── Service Functions ────────────────────────────────────────────────────────

export const fetchAllSoftSkillVendors = async (
  filters: SoftSkillVendorParamsType
) => {
  await simulateDelay();
  // TODO: const res = await axiosInstance.get("/vendors/soft-skill/", { params: filters });
  const limit = filters.limit ?? 10;
  const page = filters.page ?? 1;
  const { data, total } = applyFilters(DUMMY_SOFT_SKILL_VENDORS, filters);
  return {
    vendors: data,
    metaData: {
      pagination: { total, page, limit, totalPages: Math.ceil(total / limit) },
    } as SoftSkillVendorMetaType,
  };
};

export const fetchSoftSkillVendorById = async (_id: string) => {
  await simulateDelay();
  const vendor = DUMMY_SOFT_SKILL_VENDORS.find((v) => v._id === _id);
  if (!vendor) throw new Error("Soft skill vendor not found");
  return vendor;
};

export const createSoftSkillVendor = async (
  data: SoftSkillVendorCreateType
) => {
  await simulateDelay();
  const newVendor: SoftSkillVendorType = {
    ...data,
    _id: String(Date.now()),
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
  DUMMY_SOFT_SKILL_VENDORS.push(newVendor);
  return newVendor;
};

export const updateSoftSkillVendor = async (
  _id: string,
  data: SoftSkillVendorCreateType
) => {
  await simulateDelay();
  const index = DUMMY_SOFT_SKILL_VENDORS.findIndex((v) => v._id === _id);
  if (index === -1) throw new Error("Soft skill vendor not found");
  DUMMY_SOFT_SKILL_VENDORS[index] = {
    ...DUMMY_SOFT_SKILL_VENDORS[index],
    ...data,
    updatedAt: new Date().toISOString(),
  };
  return DUMMY_SOFT_SKILL_VENDORS[index];
};

export const deleteSoftSkillVendor = async (_id: string) => {
  await simulateDelay();
  const index = DUMMY_SOFT_SKILL_VENDORS.findIndex((v) => v._id === _id);
  if (index === -1) throw new Error("Soft skill vendor not found");
  const [deleted] = DUMMY_SOFT_SKILL_VENDORS.splice(index, 1);
  return deleted;
};

// Mock Expertise Options Fetcher
export const fetchExpertiseOptions = async (
  params: AsyncSelectFetchParams
): Promise<AsyncSelectFetchResult> => {
  await simulateDelay();
  const EXPERTISE = [
    { label: "Communication", value: "Communication" },
    { label: "Leadership", value: "Leadership" },
    { label: "Team Building", value: "Team Building" },
    { label: "Public Speaking", value: "Public Speaking" },
    { label: "Time Management", value: "Time Management" },
    { label: "Emotional Intelligence", value: "Emotional Intelligence" },
    { label: "Critical Thinking", value: "Critical Thinking" },
    { label: "Negotiation", value: "Negotiation" },
  ];

  const filtered = EXPERTISE.filter((i) =>
    i.label.toLowerCase().includes((params.search || "").toLowerCase())
  );

  return {
    items: filtered,
    hasMore: false,
    total: filtered.length,
  };
};

export const softSkillVendorService = {
  fetchAllSoftSkillVendors,
  fetchSoftSkillVendorById,
  createSoftSkillVendor,
  updateSoftSkillVendor,
  deleteSoftSkillVendor,
  fetchExpertiseOptions,
};
